// use-braintree-applepay.ts

import { useEffect } from 'react'
import { applePay } from 'braintree-web'
import { useBraintree } from './use-braintree';
import { CARD_TYPES, PAYMENT_METHOD_IDS, SupportedApplePayVersion, applePayScript} from '../utils/constants'
import useCustomSitePreference from '../hooks/forms/useCustomSitePreference'
import { asyncScriptLoader } from '../utils/utils';
import { useCustomerStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore';
import { PaymentMethodSetupData } from '../types/payments';

export function useBraintreeApplePay() {
  const merchantLabel = useCustomSitePreference('BRAINTREE_APPLEPAY_MERCHANT_LABEL', 'iceland.co.uk')
  const { createBraintreeClientInstance } = useBraintree()
  const orderStore = useOrderStore()
  const {hasApplePayButtonRendered, setHasApplePayButtonRendered } = useGlobalStore()
  const {customerInfo} = useCustomerStore()

  useEffect(() => {
    return () => {
      setHasApplePayButtonRendered(false)
    }
  }, [setHasApplePayButtonRendered])

  const initApplePayButton = async (config: PaymentMethodSetupData) => {
    await asyncScriptLoader(applePayScript)
    if (!hasApplePayButtonRendered) {
      try {
        const { btClientInstance } = await createBraintreeClientInstance()
        const btApplePayInstance = await applePay.create({ client: btClientInstance })
  
        const containers = document.querySelectorAll('apple-pay-button')

        if (!containers.length) {
          config.setErrorMessage('Apple Pay container(s) not found.')
          return
        }
  
        containers.forEach((container) => {
          container.addEventListener('click', () => {

            const paymentRequest = btApplePayInstance.createPaymentRequest({
              total: {
                label: merchantLabel,
                amount: config.isTopUp ? orderStore.topupAmount.toFixed(2) : config.amount.toFixed(2),
              },
              requiredBillingContactFields: ['postalAddress'],
              billingContact: {
                phone: config.billingAddress?.phone || customerInfo?.phoneHome,
                emailAddress: config.billingAddress?.email || customerInfo?.email,
                givenName: config.billingAddress?.firstName || customerInfo?.firstName,
                familyName: config.billingAddress?.lastName || customerInfo?.lastName,
                postalCode: config.billingAddress?.postalCode || '',
                addressLines: [config.billingAddress?.address1 || '', config.billingAddress?.address2 || ''],
                locality: config.billingAddress?.city || '',
                countryCode: config.billingAddress?.countryCode || ''
              }
            })
    const session = new ApplePaySession(SupportedApplePayVersion, paymentRequest)
            session.onvalidatemerchant = async (event) => {
              try {
                const merchantSession = await btApplePayInstance.performValidation({
                  validationURL: event.validationURL,
                  displayName: merchantLabel,
                })
                session.completeMerchantValidation(merchantSession)
              } catch (err) {
                session.abort()
                config.setErrorMessage('Merchant validation failed: ' + err)
              }
            }
  
            session.onpaymentauthorized = async (event) => {
              btApplePayInstance.tokenize(
                { token: event.payment.token },
                async (tokenizeErr, payload) => {
                  if (tokenizeErr || !payload) {
                    config.setErrorMessage('Apple Pay tokenization error: ' + (tokenizeErr || 'Undefined payload'));
                    session.completePayment(ApplePaySession.STATUS_FAILURE)
                    return
                  }
                  try {
                    await config.submitOrderProcess(payload.nonce, CARD_TYPES.APPLE_PAY, config.billingAddress, PAYMENT_METHOD_IDS.APPLE_PAY)
                    session.completePayment(ApplePaySession.STATUS_SUCCESS)
                  } catch (orderErr) {
                    config.setErrorMessage('Order processing failed: ' + orderErr)
                    session.completePayment(ApplePaySession.STATUS_FAILURE)
                  }
                }
              )
            }
            session.oncancel = (event) => {
              console.log('Apple Pay session was cancelled', event)
            } 
            session.begin()
          })
        })
        setHasApplePayButtonRendered(true)
      } catch (error) {
        config.setErrorMessage('Error initializing Apple Pay: ' + error)
      }
    }
  }

  return {
    initApplePayButton,
  }
}
